import { useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import { FilterGroup, TagOption } from 'src/types/FilterGroup';
import { ResourceListItem } from 'src/types/ResourceListItem';
import { SocialSharingOptions } from 'src/types/SocialSharingOptions';

import { queryParamFilter } from 'src/utils/searchContentQueryString';

import CardAttributeList from 'src/components/basicContent/_CardAttributeList/_CardAttributeList';
import { ShareContent } from 'src/components/basicContent/_shareContent/ShareContent';
import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import styles from './_ListingCard.module.css';

export type ListingCardProps = {
	item: ResourceListItem;
	filters: FilterGroup[] | undefined;
	wildCardUrl: string | undefined;
	favouriteButtonText: Field<string>;
	unfavouriteText: Field<string>;
	registeredUserText: Field<string>;
	rcnMemberText: Field<string>;
	rcniPlusSubscriberText: Field<string>;
	organisationalProductText: Field<string>;
	openAccessText: Field<string>;
	subjectLinkPrependedText: Field<string>;
	shareButtonToolTip: Field<string>;
	cpdHoursText: Field<string>;
	shareOptions: SocialSharingOptions;
	noShareOptions: Field<string>;
	setSubjectValue?: (name: string, value: string) => void;
};

export function ListingCard(props: ListingCardProps): JSX.Element {
	const router = useRouter();

	const allOptions: TagOption[] | undefined = props.filters?.flatMap((filter) => {
		return filter.options.map((option) => {
			const opt: TagOption = {
				filterName: filter.dtoPropertyName,
				optionId: option.title.replaceAll(' ', '-') + '_' + option.id,
				optionTitle: option.title,
			};

			return opt;
		});
	});

	const optionsToDisplay: TagOption[] | undefined = allOptions?.filter((tag) => {
		if (tag.optionTitle && props.item.Tags) {
			if (props.item.Tags.indexOf(tag.optionTitle) > -1) {
				return true;
			}
		}

		return false;
	});

	if (router.query.topics && optionsToDisplay && optionsToDisplay.length > 1) {
		const topicsIdArray: string[] = `${router.query.topics}`.split(',');

		optionsToDisplay.sort((a, b) => {
			const optionTitleA = `${a.optionTitle}`;
			const optionTitleB = `${b.optionTitle}`;
			const optionAIsSelected = topicsIdArray.includes(`${a.optionId}`);
			const optionBIsSelected = topicsIdArray.includes(`${b.optionId}`);

			if ((optionAIsSelected && !optionBIsSelected) || (optionAIsSelected && optionBIsSelected && optionTitleA < optionTitleB)) {
				return -1;
			}

			if ((optionAIsSelected && !optionBIsSelected) || (optionAIsSelected && optionBIsSelected && optionTitleA > optionTitleB)) {
				return 1;
			}

			return 0;
		});
	}

	const tagElArray: React.ReactElement[] = [];

	const isSearchPage = useMemo(() => router.asPath.includes(process.env.NEXT_PUBLIC_DEFAULT_SEARCH_PATH ?? '/Search'), []);

	if (optionsToDisplay) {
		optionsToDisplay.map((tag) => {
			const className = `${styles.subjectLink} link linkHoverWeighted`;

			tagElArray.push(
				isSearchPage ? (
					<>
						<button
							key={tag.optionId}
							className={`${className} buttonReset`}
							onClick={() => props.setSubjectValue && props.setSubjectValue(tag.filterName, tag.optionId ?? '')}
						>
							{tag.optionTitle}
						</button>
					</>
				) : (
					<a
						key={tag.optionId}
						className={className}
						href={`${props.wildCardUrl?.substring(0, props.wildCardUrl.lastIndexOf('/')) ?? ''}?q=&${queryParamFilter[tag.filterName]}=${
							tag.optionId
						}`}
					>
						{tag.optionTitle}
					</a>
				)
			);
		});
	}

	return (
		<article className={`${styles.card} greyLight`} data-item-id={props.item.Id}>
			<header>
				<EditorHelp text="Please use image with minimum 300px width and 3:2 aspect ratio (e.g. 300x200px)." />
				{props.item.ImageUrl ? (
					<Link href={props.item.Url}>
						<a className={styles.imageWrapper}>
							<img src={props.item.ImageUrl} className={styles.image} alt={props.item.Title} loading="lazy" height={200} width={300} />
						</a>
					</Link>
				) : (
					<div className={styles.image} />
				)}
				<div className={styles.subject}>
					{tagElArray && tagElArray[0] && (
						<div className={styles.primaryTag}>
							<Text field={props.subjectLinkPrependedText} />
							{tagElArray[0]}
						</div>
					)}
				</div>
			</header>
			<div className={styles.cardInfoContainer}>
				<Link href={props.item.Url}>
					<a className={styles.urlContainer}>
						<div className={styles.titleContainer}>
							<h3 className={styles.title}>{props.item.Title}</h3>
						</div>

						<div className={styles.contentCardListContainer}>
							<CardAttributeList
								item={props.item}
								filters={props.filters}
								registeredUserText={props.registeredUserText}
								rcnMemberText={props.rcnMemberText}
								rcniPlusSubscriberText={props.rcniPlusSubscriberText}
								organisationalProductText={props.organisationalProductText}
								openAccessText={props.openAccessText}
								cpdHoursText={props.cpdHoursText}
							/>
						</div>
					</a>
				</Link>

				<div className={styles.shareContentContainer}>
					<ShareContent
						shareButtonToolTip={props.shareButtonToolTip}
						shareOptions={props.shareOptions}
						noShareOptions={props.noShareOptions}
						shareTitle={props.item.Title}
						shareUrl={process.env.PUBLIC_URL + props.item.Url}
						itemId={props.item.Id}
						showText={false}
						favouriteButtonText={props.favouriteButtonText}
						unfavouriteText={props.unfavouriteText}
					/>
				</div>
			</div>
		</article>
	);
}
